import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex, HStack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { SoftwareCategorySummary } from 'app/api/services/scanVaultSoftware';
import { getIcon, StackedHeader } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { DownloadIconButton, PrimaryIconButton } from 'components/DataEntry';
import { PieChartIcon } from 'components/Icons';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';

import { selectSoftwareInventory } from '../selectors';
import { actions } from '../slice';
import {
  formatSoftwareCount,
  getSoftwareCategoryIcon,
  getSoftwareCategoryLabel,
  getSoftwareEntityCount,
  getSoftwareSubcategories,
  getSoftwareSubcategoryIcon,
  getSoftwareSubcategoryLabel,
  groupSoftwarePackages,
  OPERATING_SYSTEM_CATEGORY,
  toSoftwareReleaseRows,
} from '../utils';

import { SoftwareInventoryEntityTable } from './SoftwareInventoryEntityTable';

interface Props {
  category: SoftwareCategorySummary;
  subcategory?: string;
}

export const SoftwareInventoryCategory: FC<Props> = ({
  category,
  subcategory,
}) => {
  const dispatch = useDispatch();
  const { accountId, environmentId } = useAccessBoundary();
  const { packages, packagesNextCursor, isLoadingMorePackages } = useSelector(
    selectSoftwareInventory,
  );
  const [showSummary, setShowSummary] = useState(false);

  const isOperatingSystem = category.category === OPERATING_SYSTEM_CATEGORY;
  const selected = useMemo(
    () =>
      subcategory
        ? getSoftwareSubcategories(category).find(
            item => item.key === subcategory,
          )
        : undefined,
    [category, subcategory],
  );
  const filterSubcategory = selected?.filter.subcategory;
  const filterEcosystem = selected?.filter.ecosystem;

  const fetchPackages = useCallback(
    (cursor?: string) =>
      dispatch(
        actions.getSoftwarePackages({
          q: {
            accountId,
            environmentId,
            category: category.category,
            subcategory: filterSubcategory,
            ecosystem: filterEcosystem,
            // Operating systems are listed per release, with EOL dates.
            groupBy: isOperatingSystem ? 'version' : 'name',
            limit: 100,
            cursor,
          },
        }),
      ),
    [
      accountId,
      category.category,
      dispatch,
      environmentId,
      filterEcosystem,
      filterSubcategory,
      isOperatingSystem,
    ],
  );

  useEffect(() => {
    if (!accountId && !environmentId) return;
    fetchPackages();
  }, [accountId, environmentId, fetchPackages]);

  const title = selected
    ? getSoftwareSubcategoryLabel(category.category, selected.key)
    : getSoftwareCategoryLabel(category.category);
  const summary = selected ?? category;

  const rows = useMemo(
    () =>
      isOperatingSystem
        ? toSoftwareReleaseRows(packages.data)
        : groupSoftwarePackages(packages.data),
    [isOperatingSystem, packages.data],
  );

  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      const isNearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 100;

      if (!isNearBottom || !packagesNextCursor || isLoadingMorePackages) {
        return;
      }

      fetchPackages(packagesNextCursor);
    },
    [fetchPackages, isLoadingMorePackages, packagesNextCursor],
  );

  return (
    <Box
      h="full"
      minH={0}
      display="flex"
      flexDirection="column"
      overflowY="auto"
      px={2}
      pb={2}
    >
      <Flex justify="space-between" align="center" mb={6} px={2}>
        <StackedHeader
          type={
            selected
              ? getSoftwareSubcategoryIcon(category.category, selected.key)
              : getSoftwareCategoryIcon(category.category)
          }
          upper={title}
          lower={`${formatSoftwareCount(
            getSoftwareEntityCount(category.category, summary),
          )} ${isOperatingSystem ? 'Releases' : 'Entities'}`}
        />
        <HStack spacing={3}>
          <DownloadIconButton
            tooltip="Download software entities"
            showGrayBg
            isDisabled={!rows.length}
          />
          <PrimaryIconButton
            aria-label="Show summary"
            tooltip="Show summary"
            icon={<PieChartIcon />}
            color="primary"
            bg="transparent"
            border="1px solid"
            borderColor="componentBorder"
            onClick={() => setShowSummary(value => !value)}
          />
        </HStack>
      </Flex>

      {showSummary && (
        <HStack spacing={8} mb={6} color="gray.250">
          <Box>{formatSoftwareCount(summary.package_count)} packages</Box>
          <Box>
            {formatSoftwareCount(summary.installation_count)} installations
          </Box>
        </HStack>
      )}

      <BorderedBox
        h="full"
        w="full"
        flex={1}
        minH={0}
        display="flex"
        flexDirection="column"
        p={4}
      >
        <Box flex={1} minH={0}>
          <SoftwareInventoryEntityTable
            category={category.category}
            rows={rows}
            isLoading={packages.isLoading}
            isError={packages.isError}
            isLoadingMore={isLoadingMorePackages}
            onScroll={handleTableScroll}
          />
        </Box>
      </BorderedBox>
    </Box>
  );
};
